import { Component } from 'react';
import { useAppContext } from '@/context/AppContext/AppContext';

class ErrorBoundaryForComponentBase extends Component<
  {
    children: JSX.Element;
    customError?: string;
    logger: any;
    onError?: (error?: Error, errorInfo?: React.ErrorInfo) => void;
  },
  { hasError: boolean }
> {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.props.logger.error(error?.message || 'ErrorBoundaryForComponent caught an error', error, {
      metadata: errorInfo
    });
    this.props.onError?.(error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <h1>{this.props.customError || 'Something went wrong.'}</h1>;
    }

    return this.props.children;
  }
}

export default function ErrorBoundaryForComponent({
  children,
  customError,
  onError
}: {
  children: JSX.Element;
  customError?: string;
  onError?: (error?: Error, errorInfo?: React.ErrorInfo) => void;
}) {
  const { logger } = useAppContext();

  return (
    <ErrorBoundaryForComponentBase logger={logger} customError={customError} onError={onError}>
      {children}
    </ErrorBoundaryForComponentBase>
  );
}
